<template>
  <!-- 待上架库存(재고대기) — 库位号(위치번호) 라벨 + 扫码上架.
       스캔 순서: ① 위치번호 → ② 상품 바코드 또는 箱号(박스번호)를 연속으로. 다음 위치번호를 찍기 전까지 전부 그 위치로 재고확정.
       라벨은 입고처리 수동 박스번호 라벨(#boxLabelPrintAreaManual)과 같은 80×60mm·CODE128·$htmlToPaper 방식이다 -->
  <div class="loc-scan border rounded p-2 mb-2">
    <div class="d-flex flex-wrap align-items-start loc-scan__row">
      <!-- 위치번호 라벨 -->
      <div class="loc-scan__box">
        <div class="font-weight-bold small mb-1">🏷️ 库位号标签<small>(위치번호 라벨)</small></div>
        <b-button size="sm" variant="primary" @click="openManager">🗂️ 库位号管理·打印<small>(위치번호 관리·인쇄)</small></b-button>
        <div class="small text-muted mt-1">范围生成 → 保存 → 勾选打印(범위 생성 → 저장 → 골라서 인쇄)</div>
      </div>

      <!-- 스캔 상架 -->
      <div class="loc-scan__box flex-grow-1">
        <div class="font-weight-bold small mb-1">📷 扫码上架<small>(스캔 재고확정)</small> —
          ① 扫库位号(위치번호) → ② 连续扫商品条码 / 箱号(상품 바코드·박스번호 연속)
        </div>
        <div class="d-flex align-items-center flex-wrap loc-scan__row">
          <div :class="['loc-scan__active', { 'is-empty': !activeLocation }]">
            <small>当前库位(현재 위치)</small>
            <div>{{ activeLocation || '—' }}</div>
          </div>
          <input ref="scan" v-model="scanValue" type="text" class="form-control form-control-sm" style="width:260px"
                 :placeholder="activeLocation ? '扫商品条码或箱号(상품 바코드·박스번호)' : '先扫库位号(위치번호 먼저)'"
                 :disabled="busy" @keyup.enter="onScan">
          <b-spinner v-if="busy" small></b-spinner>
          <b-button v-if="activeLocation" size="sm" variant="outline-secondary" @click="clearLocation">结束库位<small>(위치 해제)</small></b-button>
          <b-button size="sm" variant="outline-info" @click="openPhone">📱 手机扫码上架<small>(핸드폰)</small></b-button>
        </div>
        <!-- 같은 바코드가 여러 건이면 고르게 한다 -->
        <div v-if="candidates.length" class="mt-1">
          <div class="small text-warning">多个匹配，请选择(여러 건이 맞습니다. 하나를 고르세요)</div>
          <b-button v-for="c in candidates" :key="c._id" size="sm" variant="outline-primary" class="mr-1 mt-1" @click="pickCandidate(c)">
            {{ c.companyid }} · {{ optionText(c) || c.상품명 }} · {{ qtyOf(c) }}
          </b-button>
        </div>
        <div v-if="logs.length" class="loc-scan__logs mt-1">
          <div v-for="(l, i) in logs" :key="'lg' + i" :class="l.ok ? 'text-success' : 'text-danger'">
            {{ l.time }} · {{ l.text }}
          </div>
        </div>
      </div>
    </div>

    <!-- 핸드폰으로 열기 — 직원 계정으로 로그인해서 쓴다(QR 토큰 아님) -->
    <b-modal v-model="phone.show" hide-footer size="sm" title="📱 手机扫码(핸드폰 스캔)">
      <div class="text-center">
        <img v-if="phone.qr" :src="phone.qr" alt="" style="width:220px;height:220px">
        <div class="small mt-2" style="word-break:break-all">{{ phone.url }}</div>
        <div class="small text-muted mt-1">用手机扫码打开，用员工账号登录(핸드폰으로 열고 직원 계정으로 로그인)</div>
      </div>
    </b-modal>
    <!-- 위치번호 관리: 범위 생성 · 저장 목록 · 인쇄 -->
    <b-modal v-model="manager.show" size="lg" hide-footer title="库位号管理(위치번호 관리)">
      <div class="font-weight-bold small mb-1">① 范围生成(범위 생성) — 区域-列-层(구역-열-층)</div>
      <div class="d-flex flex-wrap align-items-center loc-scan__row mb-1 small">
        <span>区域(구역)</span>
        <input v-model.trim="range.zoneFrom" maxlength="1" class="form-control form-control-sm loc-scan__n">~
        <input v-model.trim="range.zoneTo" maxlength="1" class="form-control form-control-sm loc-scan__n">
        <span class="ml-2">列(열)</span>
        <input v-model.number="range.colFrom" type="number" min="0" class="form-control form-control-sm loc-scan__n">~
        <input v-model.number="range.colTo" type="number" min="0" class="form-control form-control-sm loc-scan__n">
        <span class="ml-2">层(층)</span>
        <input v-model="range.levelFrom" type="number" min="0" class="form-control form-control-sm loc-scan__n">~
        <input v-model="range.levelTo" type="number" min="0" class="form-control form-control-sm loc-scan__n">
        <span class="ml-2">位数(자릿수)</span>
        <input v-model.number="range.pad" type="number" min="1" max="3" class="form-control form-control-sm loc-scan__n">
      </div>
      <div class="small mb-1">
        <span class="text-muted">也可直接输入(직접 입력도 가능 — 줄바꿈·쉼표)</span>
        <textarea v-model="manualText" rows="1" class="form-control form-control-sm" placeholder="A-01-01, B-02-03"></textarea>
      </div>
      <div class="small mb-2">
        预览(미리보기) {{ preview.length }}个:
        <span class="text-monospace">{{ preview.slice(0, 12).join(', ') }}{{ preview.length > 12 ? ' …' : '' }}</span>
      </div>
      <div class="mb-3">
        <b-button size="sm" variant="primary" :disabled="!preview.length || manager.busy" @click="saveLocations(false)">💾 保存到列表<small>(목록에 저장)</small></b-button>
        <b-button size="sm" variant="success" class="ml-1" :disabled="!preview.length || manager.busy" @click="saveLocations(true)">💾🖨️ 保存并打印<small>(저장 후 인쇄)</small></b-button>
        <span class="small ml-2">份数(장수)</span>
        <input v-model.number="labelCopies" type="number" min="1" :max="MAX_COPIES" class="form-control form-control-sm loc-scan__n d-inline-block">
      </div>

      <div class="font-weight-bold small mb-1">② 已保存库位号(저장된 위치번호) {{ saved.length }}个</div>
      <div class="d-flex align-items-center loc-scan__row mb-1">
        <input v-model.trim="savedFilter" class="form-control form-control-sm" style="width:160px" placeholder="搜索(검색) A-01">
        <b-button size="sm" variant="outline-primary" :disabled="!checked.length" @click="printCodes(checked)">🖨️ 打印选中<small>(선택 인쇄)</small> {{ checked.length }}</b-button>
        <b-button size="sm" variant="outline-danger" :disabled="!checked.length || manager.busy" @click="deleteLocations">🗑 删除选中<small>(선택 삭제)</small></b-button>
      </div>
      <div class="loc-scan__saved border rounded">
        <label v-for="c in filteredSaved" :key="c" class="loc-scan__chip">
          <input type="checkbox" :value="c" v-model="checked"> {{ c }}
        </label>
        <div v-if="!filteredSaved.length" class="text-muted small p-2">没有(없음)</div>
      </div>
      <div class="small mt-1">
        <a href="#" @click.prevent="checked = filteredSaved.slice()">全选(전체 선택)</a> ·
        <a href="#" @click.prevent="checked = []">取消(해제)</a>
      </div>
      <div v-if="manager.msg" :class="['small mt-2', manager.err ? 'text-danger' : 'text-success']">{{ manager.msg }}</div>
    </b-modal>

    <!-- 인쇄 영역 — 위치번호 라벨 80×60mm, 위치번호 글자 + CODE128 바코드 18mm -->
    <div :id="printAreaId" style="display:none">
      <div v-for="(lb, li) in printList" :key="'loc-' + li" style="page-break-after:always;width:100vw;height:100vh;box-sizing:border-box;padding:2mm;display:flex;flex-direction:column;justify-content:center;align-items:center;text-align:center;overflow:hidden">
        <div :style="{ fontSize: fontSize(lb) + 'px' }" style="flex:1 1 auto;min-height:0;width:100%;display:flex;align-items:center;justify-content:center;line-height:1.1;font-weight:bold;overflow-wrap:anywhere;overflow:hidden;margin-bottom:2mm">{{ lb }}</div>
        <div style="flex:0 0 18mm;width:100%;line-height:0;text-align:center"><barcode :value="lb" format="CODE128" :height="60" :width="1" :margin="4" :display-value="false"></barcode></div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import VueBarcode from 'vue-barcode';
import qrcode from 'qrcode-generator';
import { resolveScan, confirmAt, optionText, qtyOf, nowText, locationRange } from './putawayScan';

const PHONE_QUERY = '';

const MAX_COPIES = 50;
const MAX_LOGS = 50;

export default {
  name: 'LocationLabelScan',
  components: { barcode: VueBarcode },
  data() {
    return {
      MAX_COPIES,
      printAreaId: 'locLabelPrintArea' + Math.random().toString(36).slice(2, 8),
      labelCopies: 1,
      printList: [],
      // 위치번호 관리
      manager: { show: false, busy: false, msg: '', err: false },
      range: { zoneFrom: 'A', zoneTo: 'A', colFrom: 1, colTo: 10, levelFrom: 1, levelTo: 5, pad: 2 },
      manualText: '',
      saved: [],
      savedFilter: '',
      checked: [],
      // 스캔
      activeLocation: '',
      scanValue: '',
      candidates: [],
      busy: false,
      logs: [],
      refreshTimer: null,
      phone: { show: false, url: '', qr: '' }
    };
  },
  computed: {
    preview() {
      const manual = this.manualText.split(/[\n,，]/).map(s => s.trim().toUpperCase()).filter(Boolean);
      const gen = (this.range.zoneFrom && this.range.colFrom !== '' && this.range.colTo !== '') ? locationRange(this.range) : [];
      return [...new Set(manual.length ? manual : gen)];
    },
    filteredSaved() {
      const f = this.savedFilter.toUpperCase();
      return f ? this.saved.filter(c => c.includes(f)) : this.saved;
    }
  },
  beforeDestroy() {
    clearTimeout(this.refreshTimer);
  },
  methods: {
    optionText,
    qtyOf,
    openPhone() {
      const url = window.location.origin + '/m/putaway' + PHONE_QUERY;
      const qr = qrcode(0, 'M');
      qr.addData(url);
      qr.make();
      this.phone = { show: true, url, qr: qr.createDataURL(6, 4) };
    },
    // 긴 위치번호도 라벨 폭 안에 들어오게 글자 크기를 줄인다
    fontSize(lb) {
      const len = String(lb).length;
      return len <= 8 ? 56 : len <= 12 ? 44 : len <= 18 ? 32 : 24;
    },
    printCodes(codes) {
      const copies = Math.min(MAX_COPIES, Math.max(1, parseInt(this.labelCopies) || 1));
      this.labelCopies = copies;
      const list = [];
      codes.forEach(lb => { for (let i = 0; i < copies; i++) list.push(lb); });
      this.printList = list;
      this.$nextTick(() => {
        // 박스 라벨과 같은 인쇄 설정(80×60mm, 바코드 높이 18mm)
        const css = '@page{size:80mm 60mm;margin:0}html,body{margin:0;padding:0}svg{height:18mm;width:auto;max-width:92%}';
        this.$htmlToPaper(this.printAreaId, { name: '_blank', autoClose: true, styles: ['data:text/css;charset=utf-8,' + encodeURIComponent(css)] });
        setTimeout(() => { this.printList = []; }, 2000);
      });
    },
    // ── 위치번호 관리 ──
    managerSay(msg, err) {
      this.manager.msg = msg;
      this.manager.err = !!err;
    },
    async openManager() {
      this.manager.show = true;
      this.managerSay('');
      await this.loadSaved();
    },
    async loadSaved() {
      try {
        const { data } = await axios.post('/api/warehouse/locations/list', {});
        if (data.요청결과 !== '성공') throw new Error((data.error && data.error.errorMessage) || '');
        this.saved = (data.locations || []).map(l => l.code);
      } catch (e) {
        console.error('위치번호 목록 오류:', e);
        this.managerSay('读取失败(목록 읽기 실패): ' + e.message, true);
      }
    },
    async saveLocations(andPrint) {
      const codes = this.preview.slice();
      this.manager.busy = true;
      try {
        const { data } = await axios.post('/api/warehouse/locations/save', { codes });
        if (data.요청결과 !== '성공') throw new Error((data.error && data.error.errorMessage) || '');
        this.managerSay('已保存(저장) ' + codes.length + '个 · 新增(새로) ' + (data.added || 0));
        await this.loadSaved();
        if (andPrint) this.printCodes(codes);
      } catch (e) {
        console.error('위치번호 저장 오류:', e);
        this.managerSay('保存失败(저장 실패): ' + e.message, true);
      } finally {
        this.manager.busy = false;
      }
    },
    async deleteLocations() {
      if (!confirm('删除选中的 ' + this.checked.length + ' 个库位号?(선택한 위치번호를 목록에서 지웁니다. 이미 등록된 재고 위치는 그대로입니다)')) return;
      this.manager.busy = true;
      try {
        const { data } = await axios.post('/api/warehouse/locations/delete', { codes: this.checked });
        if (data.요청결과 !== '성공') throw new Error((data.error && data.error.errorMessage) || '');
        this.managerSay('已删除(삭제) ' + (data.deleted || 0));
        this.checked = [];
        await this.loadSaved();
      } catch (e) {
        console.error('위치번호 삭제 오류:', e);
        this.managerSay('删除失败(삭제 실패): ' + e.message, true);
      } finally {
        this.manager.busy = false;
      }
    },
    // ── 스캔 ──
    log(text, ok) {
      this.logs.unshift({ time: nowText(), text, ok: !!ok });
      if (this.logs.length > MAX_LOGS) this.logs.length = MAX_LOGS;
    },
    focus() {
      this.$nextTick(() => { if (this.$refs.scan) this.$refs.scan.focus(); });
    },
    clearLocation() {
      this.activeLocation = '';
      this.candidates = [];
      this.focus();
    },
    // 부모 목록 새로고침은 연속 스캔 중에 매번 하지 않고 몰아서 한 번
    refreshParent() {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = setTimeout(() => this.$emit('confirmed'), 1500);
    },
    async confirmItems(items, label) {
      const loc = this.activeLocation;
      const { results, skipped } = await confirmAt(items, loc);
      results.forEach(r => {
        const it = items.find(x => String(x._id) === String(r._id)) || {};
        this.log('✅ ' + (it.상품명 || it.바코드 || r._id) + ' ' + optionText(it) + ' → ' + loc + ' 已上架(재고확정)' + (label ? ' [' + label + ']' : ''), true);
      });
      skipped.forEach(s => {
        const it = items.find(x => String(x._id) === String(s._id)) || {};
        this.log('✖ ' + (it.상품명 || s._id) + ' — ' + s.reason, false);
      });
      if (results.length) this.refreshParent();
    },
    async pickCandidate(c) {
      this.candidates = [];
      this.busy = true;
      try {
        await this.confirmItems([c]);
      } catch (e) {
        console.error('스캔 재고확정 오류:', e);
        this.log('上架错误(재고확정 오류): ' + e.message, false);
      } finally {
        this.busy = false;
        this.focus();
      }
    },
    // 스캐너 = 키보드 입력 + Enter
    async onScan() {
      const v = (this.scanValue || '').trim();
      this.scanValue = '';
      if (!v) return;
      this.candidates = [];
      this.busy = true;
      try {
        const res = await resolveScan(v);
        if (res.type === 'location') {
          this.activeLocation = res.location;
          this.log('📍 库位(위치) ' + res.location + (res.saved ? '' : ' (未保存的库位号/목록에 없는 위치번호)'), true);
        } else if (!this.activeLocation) {
          this.log('请先扫库位号(위치번호를 먼저 스캔하세요): ' + v, false);
        } else if (res.type === 'box') {
          if (!res.items.length) {
            this.log('📦 ' + v + ' — 箱内没有待上架商品(박스에 재고대기 상품 없음)' + (res.others && res.others.length ? ' · ' + res.others.map(o => o.processStatus).join(',') : ''), false);
          } else {
            await this.confirmItems(res.items, '📦 ' + v);
          }
        } else if (res.type === 'product') {
          if (res.items.length > 1) {
            this.candidates = res.items;
          } else {
            await this.confirmItems(res.items);
          }
        } else {
          this.log('未找到待上架商品/箱号(재고대기 상품·박스가 아닙니다): ' + v, false);
        }
      } catch (e) {
        console.error('스캔 처리 오류:', e);
        this.log('错误(오류): ' + e.message, false);
      } finally {
        this.busy = false;
        this.focus();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.loc-scan {
  &__row { gap: 8px; }
  &__box { min-width: 260px; }
  &__active {
    min-width: 150px;
    padding: 2px 10px;
    border: 2px solid #28a745;
    border-radius: 6px;
    background: #eafaf0;
    small { color: #555; }
    div { font-size: 26px; font-weight: 700; line-height: 1.1; color: #1e7e34; }
    &.is-empty { border-color: #ccc; background: #f6f6f6; div { color: #999; } }
  }
  &__logs {
    max-height: 150px;
    overflow-y: auto;
    font-size: 12px;
    background: #f8f9fa;
    padding: 4px 8px;
    border-radius: 4px;
  }
  &__n { width: 64px; }
  &__saved {
    max-height: 220px;
    overflow-y: auto;
    padding: 4px;
  }
  &__chip {
    display: inline-block;
    margin: 2px 8px 2px 0;
    font-family: monospace;
    font-size: 13px;
  }
}
</style>
